import { type ReactNode } from 'react';
import { ClipLoader } from 'react-spinners';
import { ClickAwayListener, Column, Row, Text } from '~/commonComponents';
import ArrowDownIcon from '~/images/icons/ArrowDown';
import { Colors } from '~/themes/colors';

interface IFormDropdownProps {
  label: string;
  labelRight?: ReactNode;
  prefix?: ReactNode;
  value?: string;
  placeholder?: string;
  isOpen: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onToggle: () => void;
  children: ReactNode;
  testId?: string;
  footer?: ReactNode;
}

export const FormDropdown = ({
  label,
  labelRight,
  prefix,
  value,
  placeholder = 'Select',
  isOpen,
  isLoading,
  disabled,
  onToggle,
  children,
  testId,
  footer,
}: IFormDropdownProps) => {
  const handleClickAway = () => {
    if (isOpen) {
      onToggle();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Column>
        <Column gap={8}>
          <Row alignItems="center" gap={6}>
            <Text bold fontSize={18} color="neutral_600">
              {label}:
            </Text>
            {labelRight}
          </Row>

          <Row
            alignItems="center"
            gap={8}
            onClick={disabled ? undefined : onToggle}
            style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
            data-testid={testId}
            data-disabled={disabled}
          >
            {prefix}
            <div style={{ position: 'relative', flex: 1 }}>
              <Row
                height={40}
                borderRadius={10}
                backgroundColor={Colors.neutral_50}
                alignItems="center"
                paddingHorizontal={14}
                style={{
                  boxShadow: 'inset 0px 2px 4px rgba(0,0,0,0.25)',
                }}
              >
                <Text fontSize={16} color={value ? 'neutral_600' : 'neutral_300'} flex={1}>
                  {value || placeholder}
                </Text>
                <ArrowDownIcon color={Colors.secondary_500} />
              </Row>

              {isOpen && (
                <Column
                  borderRadius={10}
                  style={{
                    position: 'absolute',
                    top: '0%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    backgroundColor: Colors.neutral_150,
                    boxShadow: '0px 2px 3px rgba(0,0,0,0.15), 0px 6px 10px 2px rgba(0,0,0,0.15)',
                    maxHeight: 240,
                    overflow: 'auto',
                    overscrollBehavior: 'contain',
                    marginTop: 4,
                    padding: 8,
                  }}
                >
                  {isLoading ? (
                    <Row
                      justifyContent="center"
                      alignItems="center"
                      style={{ padding: '12px 0' }}
                      data-testid="dropdown-loading"
                    >
                      <ClipLoader size={24} color={Colors.secondary_500} loading />
                    </Row>
                  ) : (
                    children
                  )}
                </Column>
              )}
            </div>
          </Row>
        </Column>

        {footer}
      </Column>
    </ClickAwayListener>
  );
};
